<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Konva from 'konva'
import { v4 } from 'uuid'
import {
  adjustCrop,
  type AnnotationShape,
  createImageEdit,
  type CropHandle,
  FULL_CROP,
  type ImageCrop,
  type ImageEdit,
  type LineShape,
  scaleAnnotations,
  type TextShape,
} from '@/utils/imageEdit'
import { createAnnotationNode, readAnnotationNode, TEXT_FONT_FAMILY } from '@/utils/annotationNodes'
import { createUndoHistory } from '@/utils/undoHistory'

export type EditorMode = 'annotate' | 'crop'
export type AnnotationTool = 'select' | 'text' | 'rectangle' | 'ellipse' | 'line' | 'arrow'

const props = defineProps<{
  image: HTMLImageElement
  initialEdit?: ImageEdit
  mode: EditorMode
}>()
const emit = defineEmits<{ dirty: [] }>()

const FIT_MARGIN = 24
const MIN_DRAW_SIZE = 4
const ANCHOR_RADIUS = 7
const SELECTION_COLOR = '#1976d2'
const LINE_ANCHOR = 'line-anchor'
const BOX_ANCHORS = [
  'top-left',
  'top-center',
  'top-right',
  'middle-right',
  'bottom-right',
  'bottom-center',
  'bottom-left',
  'middle-left',
]
const TEXT_ANCHORS = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
const CROP_HANDLES: Exclude<CropHandle, 'move'>[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

const imageWidth = props.image.naturalWidth
const imageHeight = props.image.naturalHeight
// Sizes scale with the image so annotations look the same on small and large uploads.
const unit = Math.max(imageWidth, imageHeight) / 400

const container = ref<HTMLDivElement>()
const textEditor = ref<HTMLTextAreaElement>()
const tool = ref<AnnotationTool>('select')
const color = ref('#e53935')
const thickness = ref(2)
const selectedId = ref<string>()
const canUndo = ref(false)
const canRedo = ref(false)
const crop = ref<ImageCrop>(props.initialEdit?.crop ?? FULL_CROP)
const view = ref({ x: 0, y: 0, zoom: 1 })
const editingText = ref<{ id: string; isNew: boolean; style: Record<string, string> }>()

const initialShapes = props.initialEdit?.annotations
  ? scaleAnnotations(props.initialEdit.annotations, imageWidth, imageHeight).shapes
  : []
let shapes: AnnotationShape[] = structuredClone(initialShapes)
const history = createUndoHistory<AnnotationShape[]>(structuredClone(initialShapes))

let stage: Konva.Stage | undefined
let content: Konva.Group | undefined
let shapesGroup: Konva.Group | undefined
let transformer: Konva.Transformer | undefined
let draft: { id: string; startX: number; startY: number } | undefined
let cropDrag: { handle: CropHandle; x: number; y: number; start: ImageCrop } | undefined

const hasSelection = computed(() => !!selectedId.value)
const strokeWidth = () => Math.max(1, Math.round((unit * thickness.value) / 4))
const fontSize = () => Math.round(unit * (thickness.value + 3) * 4)
const findShape = (id: string | undefined) => shapes.find((shape) => shape.id === id)

const syncHistory = () => {
  canUndo.value = history.canUndo
  canRedo.value = history.canRedo
}

const commit = () => {
  history.record(structuredClone(shapes))
  syncHistory()
  emit('dirty')
}

const pointer = () => content?.getRelativePointerPosition() ?? { x: 0, y: 0 }

const replaceShape = (updated: AnnotationShape) => {
  shapes = shapes.map((shape) => (shape.id === updated.id ? updated : shape))
}

const removeShape = (id: string) => {
  shapes = shapes.filter((shape) => shape.id !== id)
}

const addLineAnchors = (shape: LineShape, node: Konva.Arrow) => {
  node.on('dragstart', () => shapesGroup?.find(`.${LINE_ANCHOR}`).forEach((a) => a.destroy()))
  ;[0, 2].forEach((index) => {
    const anchor = new Konva.Circle({
      name: LINE_ANCHOR,
      x: shape.points[index],
      y: shape.points[index + 1],
      radius: ANCHOR_RADIUS / view.value.zoom,
      fill: 'white',
      stroke: SELECTION_COLOR,
      strokeWidth: 2 / view.value.zoom,
      draggable: true,
    })
    anchor.on('dragmove', () => {
      const points = [...node.points()]
      points[index] = anchor.x()
      points[index + 1] = anchor.y()
      node.points(points)
    })
    anchor.on('dragend', () => {
      replaceShape({ ...shape, points: node.points() as LineShape['points'] })
      commit()
      redraw()
    })
    shapesGroup?.add(anchor)
  })
}

const attachSelection = () => {
  if (!shapesGroup || !transformer) return
  transformer.nodes([])
  shapesGroup.find(`.${LINE_ANCHOR}`).forEach((anchor) => anchor.destroy())

  const selected = findShape(selectedId.value)
  const selectedNode = selected && shapesGroup.findOne<Konva.Shape>(`#${selected.id}`)
  if (selected && selectedNode && props.mode === 'annotate' && tool.value === 'select') {
    if (selected.type === 'line' || selected.type === 'arrow') {
      addLineAnchors(selected, selectedNode as Konva.Arrow)
    } else {
      transformer.keepRatio(selected.type === 'text')
      transformer.enabledAnchors(selected.type === 'text' ? TEXT_ANCHORS : BOX_ANCHORS)
      transformer.nodes([selectedNode])
    }
  }
  stage?.batchDraw()
}

const redraw = () => {
  if (!shapesGroup || !transformer) return
  transformer.nodes([])
  shapesGroup.destroyChildren()
  const interactive = props.mode === 'annotate' && tool.value === 'select'

  shapes.forEach((shape) => {
    if (editingText.value?.id === shape.id) return
    const node = createAnnotationNode(shape)
    node.draggable(interactive)
    node.listening(interactive)
    node.on('pointerdown', (event) => {
      event.cancelBubble = true
      select(shape.id)
    })
    node.on('dragend transformend', () => {
      replaceShape(readAnnotationNode(node, shape))
      commit()
      redraw()
    })
    if (shape.type === 'text') node.on('dblclick dbltap', () => startTextEditing(shape.id, false))
    shapesGroup?.add(node)
  })
  attachSelection()
}

const select = (id: string | undefined) => {
  selectedId.value = id
  const shape = findShape(id)
  if (shape) color.value = shape.color
  attachSelection()
}

const newShape = (
  type: Exclude<AnnotationTool, 'select'>,
  x: number,
  y: number,
): AnnotationShape => {
  const style = { id: v4(), color: color.value, strokeWidth: strokeWidth() }
  switch (type) {
    case 'text':
      return { ...style, type, x, y, text: '', fontSize: fontSize(), rotation: 0 }
    case 'line':
    case 'arrow':
      return { ...style, type, points: [x, y, x, y] }
    default:
      return { ...style, type, x, y, width: 0, height: 0, rotation: 0 }
  }
}

const onStagePointerDown = (event: Konva.KonvaEventObject<PointerEvent>) => {
  if (props.mode !== 'annotate' || editingText.value) return
  if (tool.value === 'select') {
    if (event.target === stage || event.target.getParent() === content) select(undefined)
    return
  }

  const { x, y } = pointer()
  const shape = newShape(tool.value, x, y)
  shapes = [...shapes, shape]
  selectedId.value = undefined
  if (shape.type === 'text') {
    tool.value = 'select'
    startTextEditing(shape.id, true)
    return
  }
  draft = { id: shape.id, startX: x, startY: y }
  redraw()
}

const onStagePointerMove = () => {
  const shape = findShape(draft?.id)
  if (!draft || !shape) return
  const { x, y } = pointer()
  const { startX, startY } = draft
  if (shape.type === 'line' || shape.type === 'arrow') {
    replaceShape({ ...shape, points: [startX, startY, x, y] })
  } else if (shape.type === 'rectangle' || shape.type === 'ellipse') {
    replaceShape({
      ...shape,
      x: (startX + x) / 2,
      y: (startY + y) / 2,
      width: Math.abs(x - startX),
      height: Math.abs(y - startY),
    })
  }
  redraw()
}

const onStagePointerUp = () => {
  if (!draft) return
  const { id, startX, startY } = draft
  draft = undefined
  const { x, y } = pointer()
  if (Math.hypot(x - startX, y - startY) * view.value.zoom < MIN_DRAW_SIZE) {
    removeShape(id)
    redraw()
    return
  }
  tool.value = 'select'
  commit()
  select(id)
}

const startTextEditing = (id: string, isNew: boolean) => {
  const shape = findShape(id) as TextShape | undefined
  if (!shape) return
  const { x, y, zoom } = view.value
  editingText.value = {
    id,
    isNew,
    style: {
      left: `${x + shape.x * zoom}px`,
      top: `${y + shape.y * zoom}px`,
      fontSize: `${shape.fontSize * zoom}px`,
      fontFamily: TEXT_FONT_FAMILY,
      color: shape.color,
      transform: `rotate(${shape.rotation}deg)`,
    },
  }
  selectedId.value = undefined
  redraw()
  requestAnimationFrame(() => {
    if (!textEditor.value) return
    textEditor.value.value = shape.text
    textEditor.value.focus()
    textEditor.value.select()
  })
}

const finishTextEditing = (keep: boolean) => {
  const editing = editingText.value
  if (!editing) return
  const shape = findShape(editing.id) as TextShape | undefined
  const text = textEditor.value?.value.trim() ?? ''
  editingText.value = undefined
  if (!shape) return redraw()

  if (keep && text) {
    replaceShape({ ...shape, text })
    commit()
    selectedId.value = shape.id
    redraw()
  } else if (editing.isNew || !text) {
    removeShape(shape.id)
    if (!editing.isNew) commit()
    redraw()
  } else {
    redraw()
  }
}

const onTextKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    finishTextEditing(true)
  } else if (event.key === 'Escape') {
    event.stopPropagation()
    finishTextEditing(false)
  }
}

const setTool = (value: AnnotationTool) => {
  finishTextEditing(true)
  tool.value = value
  if (value !== 'select') selectedId.value = undefined
  redraw()
}

const updateSelected = (update: (shape: AnnotationShape) => AnnotationShape) => {
  const shape = findShape(selectedId.value)
  if (!shape) return
  replaceShape(update(shape))
  commit()
  redraw()
}

const setColor = (value: string) => {
  color.value = value
  updateSelected((shape) => ({ ...shape, color: value }))
}

const setThickness = (value: number) => {
  thickness.value = value
  updateSelected((shape) =>
    shape.type === 'text'
      ? { ...shape, fontSize: fontSize() }
      : { ...shape, strokeWidth: strokeWidth() },
  )
}

const restoreShapes = (snapshot: AnnotationShape[] | undefined) => {
  if (!snapshot) return
  shapes = structuredClone(snapshot)
  if (!findShape(selectedId.value)) selectedId.value = undefined
  syncHistory()
  emit('dirty')
  redraw()
}

const undo = () => restoreShapes(history.undo())
const redo = () => restoreShapes(history.redo())

const deleteSelected = () => {
  if (!selectedId.value) return
  removeShape(selectedId.value)
  selectedId.value = undefined
  commit()
  redraw()
}

const removeAllEdits = () => {
  finishTextEditing(false)
  crop.value = FULL_CROP
  shapes = []
  selectedId.value = undefined
  commit()
  redraw()
}

const resetCrop = () => {
  crop.value = FULL_CROP
  emit('dirty')
}

const getEdit = (): ImageEdit =>
  createImageEdit(crop.value, { width: imageWidth, height: imageHeight, shapes })

const render = (mimeType: string, quality?: number): Promise<Blob> => {
  const offscreen = new Konva.Stage({
    container: document.createElement('div'),
    width: imageWidth,
    height: imageHeight,
  })
  const layer = new Konva.Layer()
  offscreen.add(layer)
  layer.add(new Konva.Image({ image: props.image, width: imageWidth, height: imageHeight }))
  shapes.forEach((shape) => layer.add(createAnnotationNode(shape)))

  const { x, y, w, h } = crop.value
  const canvas = offscreen.toCanvas({
    x: Math.round(x * imageWidth),
    y: Math.round(y * imageHeight),
    width: Math.max(1, Math.round(w * imageWidth)),
    height: Math.max(1, Math.round(h * imageHeight)),
    pixelRatio: 1,
  })
  offscreen.destroy()

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not export the image'))),
      mimeType,
      quality,
    ),
  )
}

const fitToView = () => {
  if (!stage || !content || !container.value) return
  const { clientWidth, clientHeight } = container.value
  const zoom = Math.min(
    (clientWidth - 2 * FIT_MARGIN) / imageWidth,
    (clientHeight - 2 * FIT_MARGIN) / imageHeight,
  )
  if (zoom <= 0) return
  view.value = {
    x: (clientWidth - imageWidth * zoom) / 2,
    y: (clientHeight - imageHeight * zoom) / 2,
    zoom,
  }
  stage.size({ width: clientWidth, height: clientHeight })
  content.position({ x: view.value.x, y: view.value.y })
  content.scale({ x: zoom, y: zoom })
  redraw()
}
const resizeObserver = new ResizeObserver(fitToView)

const frameStyle = computed(() => {
  const { x, y, zoom } = view.value
  return {
    left: `${x + crop.value.x * imageWidth * zoom}px`,
    top: `${y + crop.value.y * imageHeight * zoom}px`,
    width: `${crop.value.w * imageWidth * zoom}px`,
    height: `${crop.value.h * imageHeight * zoom}px`,
  }
})

const moveCropDrag = (event: PointerEvent) => {
  if (!cropDrag) return
  const dx = (event.clientX - cropDrag.x) / (imageWidth * view.value.zoom)
  const dy = (event.clientY - cropDrag.y) / (imageHeight * view.value.zoom)
  crop.value = adjustCrop(cropDrag.start, cropDrag.handle, dx, dy)
}

const endCropDrag = () => {
  window.removeEventListener('pointermove', moveCropDrag)
  window.removeEventListener('pointerup', endCropDrag)
  if (cropDrag) emit('dirty')
  cropDrag = undefined
}

const startCropDrag = (handle: CropHandle, event: PointerEvent) => {
  if (props.mode !== 'crop') return
  event.preventDefault()
  cropDrag = { handle, x: event.clientX, y: event.clientY, start: crop.value }
  window.addEventListener('pointermove', moveCropDrag)
  window.addEventListener('pointerup', endCropDrag)
}

const onKeydown = (event: KeyboardEvent) => {
  if (editingText.value || props.mode !== 'annotate' || !selectedId.value) return
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
    return
  if (event.key === 'Delete' || event.key === 'Backspace') {
    event.preventDefault()
    deleteSelected()
  }
}

onMounted(() => {
  if (!container.value) return
  stage = new Konva.Stage({
    container: container.value,
    width: container.value.clientWidth,
    height: container.value.clientHeight,
  })
  const layer = new Konva.Layer()
  content = new Konva.Group()
  shapesGroup = new Konva.Group()
  transformer = new Konva.Transformer({
    ignoreStroke: true,
    anchorStroke: SELECTION_COLOR,
    borderStroke: SELECTION_COLOR,
    anchorSize: 10,
  })
  content.add(new Konva.Image({ image: props.image, width: imageWidth, height: imageHeight }))
  content.add(shapesGroup)
  layer.add(content)
  layer.add(transformer)
  stage.add(layer)

  stage.on('pointerdown', onStagePointerDown)
  stage.on('pointermove', onStagePointerMove)
  stage.on('pointerup', onStagePointerUp)
  window.addEventListener('keydown', onKeydown)
  resizeObserver.observe(container.value)
  fitToView()
  syncHistory()
})

onBeforeUnmount(() => {
  resizeObserver.disconnect()
  endCropDrag()
  window.removeEventListener('keydown', onKeydown)
  stage?.destroy()
})

watch(
  () => props.mode,
  () => {
    finishTextEditing(true)
    selectedId.value = undefined
    tool.value = 'select'
    redraw()
  },
)

const cursor = computed(() => {
  if (props.mode !== 'annotate' || tool.value === 'select') return 'default'
  return tool.value === 'text' ? 'text' : 'crosshair'
})

defineExpose({
  tool,
  color,
  thickness,
  canUndo,
  canRedo,
  hasSelection,
  setTool,
  setColor,
  setThickness,
  undo,
  redo,
  deleteSelected,
  removeAllEdits,
  resetCrop,
  getEdit,
  render,
})
</script>

<template>
  <div class="image-editor-canvas" :class="{ cropping: mode === 'crop' }" :style="{ cursor }">
    <div ref="container" class="image-editor-stage" />
    <textarea
      v-if="editingText"
      ref="textEditor"
      class="text-editor"
      :style="editingText.style"
      rows="1"
      @keydown="onTextKeydown"
      @blur="finishTextEditing(true)"
    />
    <div class="crop-frame" :style="frameStyle" @pointerdown.self="startCropDrag('move', $event)">
      <template v-if="mode === 'crop'">
        <div
          v-for="handle in CROP_HANDLES"
          :key="handle"
          :class="['crop-handle', `crop-handle-${handle}`]"
          @pointerdown.stop="startCropDrag(handle, $event)"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.image-editor-canvas {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.image-editor-stage {
  position: absolute;
  inset: 0;
}

.text-editor {
  position: absolute;
  z-index: 2;
  min-width: 120px;
  margin: 0;
  padding: 0;
  border: 1px dashed #1976d2;
  outline: none;
  background: transparent;
  line-height: 1.2;
  resize: none;
  overflow: hidden;
  white-space: pre;
  field-sizing: content;
  transform-origin: top left;
}

.crop-frame {
  position: absolute;
  z-index: 1;
  pointer-events: none;
  touch-action: none;
  outline: 1px dashed rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.35);
}

.cropping .crop-frame {
  pointer-events: auto;
  cursor: move;
  outline: 2px solid rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
}

.crop-handle {
  position: absolute;
  width: 14px;
  height: 14px;
  margin: -7px 0 0 -7px;
  background: white;
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 50%;
  touch-action: none;
}

.crop-handle-nw {
  left: 0;
  top: 0;
  cursor: nwse-resize;
}
.crop-handle-n {
  left: 50%;
  top: 0;
  cursor: ns-resize;
}
.crop-handle-ne {
  left: 100%;
  top: 0;
  cursor: nesw-resize;
}
.crop-handle-e {
  left: 100%;
  top: 50%;
  cursor: ew-resize;
}
.crop-handle-se {
  left: 100%;
  top: 100%;
  cursor: nwse-resize;
}
.crop-handle-s {
  left: 50%;
  top: 100%;
  cursor: ns-resize;
}
.crop-handle-sw {
  left: 0;
  top: 100%;
  cursor: nesw-resize;
}
.crop-handle-w {
  left: 0;
  top: 50%;
  cursor: ew-resize;
}
</style>
